/**
 * «🖐 Ждут вашего решения» — `#decisionBlock` (ui-blocks.md §5.2).
 *
 * Четыре причины приходят с бэкенда (`decisions.rows[].reason`):
 * priority1 · approval · extend · escalation · unassigned.
 * По ТЗ §2 главврач решает прямо из строки: согласовать ответ,
 * утвердить продление, назначить исполнителя.
 */

import { useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { invalidateCase, queryKeys } from '@/api/hooks';
import { errorMessage } from '@/components/ErrorState';
import { BLOCK_GAP, HEAD_NOTE, caseWord, deadlineClass } from './format';
import type {
    DecisionReason,
    DecisionRow,
    DecisionsBlock,
    LoadRow,
    OpenCase,
    ShowToast,
} from './types';

/** Цвет плашки причины. База `.dr-reason` — красная, остальные три — модификаторы. */
const REASON_CLASS: Record<DecisionReason, string> = {
    priority1: '',
    approval: 'approval',
    extend: 'extend',
    escalation: 'escalation',
    unassigned: '',
};

interface ActVars {
    caseId: string;
    path: string;
    body?: unknown;
}

interface Props {
    decisions: DecisionsBlock | null;
    /** Исполнители из блока нагрузки — список для селекта «Назначить». */
    executors: LoadRow[];
    openCase: OpenCase;
    toast: ShowToast;
}

export function DecisionBlock({ decisions, executors, openCase, toast }: Props) {
    const queryClient = useQueryClient();
    const [assignee, setAssignee] = useState<Record<string, string>>({});
    const [busyId, setBusyId] = useState<string | null>(null);

    const act = useMutation({
        mutationFn: (vars: ActVars) => api.post<unknown>(`/cases/${vars.caseId}${vars.path}`, vars.body),
        onMutate: (vars: ActVars) => {
            setBusyId(vars.caseId);
        },
        onSuccess: (data: unknown, vars: ActVars) => {
            if (vars.path === '/approve') toast('Ответ согласован');
            else if (vars.path === '/extend/decide') toast('Продление утверждено');
            else toast('Исполнитель назначен');
            // Точечно: карточка дела, списки дел и дашборд. Плюс колокольчик —
            // все три действия порождают уведомление исполнителю.
            invalidateCase(queryClient, vars.caseId, data);
            void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
            void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
        },
        onError: (error: unknown) => {
            toast(errorMessage(error) || 'Не удалось выполнить действие');
        },
        onSettled: () => {
            setBusyId(null);
        },
    });

    const rows = decisions?.rows ?? [];
    const total = decisions?.total ?? 0;
    const rest = total - rows.length;

    const stop = (event: MouseEvent | KeyboardEvent): void => {
        event.stopPropagation();
    };

    const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, row: DecisionRow): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openCase(row.case_id);
    };

    const renderAction = (row: DecisionRow) => {
        const busy = busyId === row.case_id;

        if (row.reason === 'approval') {
            return (
                <button
                    type="button"
                    className="act-btn primary"
                    disabled={busy}
                    onClick={(event) => {
                        stop(event);
                        act.mutate({ caseId: row.case_id, path: '/approve' });
                    }}
                >
                    Согласовать
                </button>
            );
        }

        if (row.reason === 'extend') {
            return (
                <button
                    type="button"
                    className="act-btn warn"
                    disabled={busy}
                    onClick={(event) => {
                        stop(event);
                        act.mutate({
                            caseId: row.case_id,
                            path: '/extend/decide',
                            body: { approved: true },
                        });
                    }}
                >
                    Утвердить продление
                </button>
            );
        }

        if (row.reason === 'unassigned') {
            const chosen = assignee[row.case_id] ?? '';
            return (
                <>
                    <select
                        className="act-select"
                        aria-label="Кому назначить дело"
                        value={chosen}
                        disabled={busy}
                        onClick={stop}
                        onKeyDown={stop}
                        onChange={(event) => {
                            const value = event.target.value;
                            setAssignee((prev) => ({ ...prev, [row.case_id]: value }));
                        }}
                    >
                        <option value="">Кому назначить…</option>
                        {executors.map((executor) => (
                            <option key={executor.executor_id} value={executor.executor_id}>
                                {executor.full_name}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="act-btn"
                        disabled={busy || chosen === ''}
                        onClick={(event) => {
                            stop(event);
                            if (chosen === '') return;
                            act.mutate({
                                caseId: row.case_id,
                                path: '/assign',
                                body: { executor_id: chosen },
                            });
                        }}
                    >
                        Назначить
                    </button>
                </>
            );
        }

        return null;
    };

    return (
        <div className="stat-block" style={BLOCK_GAP}>
            <div className="block-header">
                <span>
                    🖐 Ждут вашего решения{' '}
                    <span id="decisionCount" style={HEAD_NOTE}>
                        {total > 0 ? `· ${total} ${caseWord(total)}` : ''}
                    </span>
                </span>
            </div>
            <div className="block-body" id="decisionBlock">
                {rows.length === 0 ? (
                    <div className="detail-empty">
                        Решений не требуется — всё в работе у исполнителей.
                    </div>
                ) : (
                    <>
                        {rows.map((row) => (
                            <div
                                key={row.case_id}
                                className="decision-row"
                                data-case={row.case_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openCase(row.case_id)}
                                onKeyDown={(event) => onRowKeyDown(event, row)}
                            >
                                <span className={`dr-reason ${REASON_CLASS[row.reason]}`.trim()}>
                                    {row.reason_label}
                                </span>
                                <span className="dr-id">{row.number}</span>
                                <span className="dr-title">
                                    {row.title}
                                    {row.escalation_risk ? ' 🛡' : ''}
                                </span>
                                <span className="dr-exec">{row.executor_name ?? '—'}</span>
                                <span className={`dr-date ${deadlineClass(row.deadline_state)}`.trim()}>
                                    {row.deadline_text}
                                </span>
                                {renderAction(row)}
                            </div>
                        ))}
                        {rest > 0 && (
                            <div className="case-more">
                                и ещё {rest} {caseWord(rest)}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
